<template>
  <div class="d-flex justify-content-between align-items-center">
    <div class="form-label">{{ $t('campaigns.google_ads.descriptions') }}</div>
    <small v-if="maxDescriptions" class="text-muted">
      {{ $t('campaigns.google_ads.description_count', { current: descriptions.length, max: maxDescriptions }) }}
    </small>
  </div>
  <div class="d-flex flex-column gap-2">
    <template v-for="(description, index) in descriptions" :key="index">
      <div class="d-flex gap-2 align-items-center">
        <LeadInput
          v-bind="$attrs"
          type="textarea"
          :value="description"
          @update:modelValue="updateDescription(index, $event)"
          class="flex-grow-1"
          rows="4"
          :label="`#${index + 1}`" />
        <LeadButton
          v-if="descriptions.length > 1"
          variant="white"
          @click="descriptions = descriptions.filter((_, i) => i !== index)"
          class="align-self-end">
          <template #prepend-icon>
            <IconTrashFilled size="16" />
          </template>
        </LeadButton>
      </div>
    </template>
    <LeadButton variant="secondary" class="float-right" :disabled="!canAddDescription" @click="addDescription">
      <template #prepend-icon>
        <IconPlus size="16" />
      </template>
    </LeadButton>
  </div>

  <div v-if="props.errors && Object.keys(props.errors).length" class="mt-2">
    <FormErrorList :errors="Object.values(props.errors).flat()" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import LeadInput from './LeadInput.vue';
import LeadButton from '../generic/LeadButton.vue';
import { IconPlus, IconTrashFilled } from '@tabler/icons-vue';
import { FormErrorList } from '@prospective/lithium';
import type { TextAssetOptions } from '@/types/types';

const props = withDefaults(
  defineProps<{
    modelValue: string | null | undefined;
    options?: TextAssetOptions;
    errors?: Record<string, string[] | string[]>;
  }>(),
  {
    modelValue: '',
    options: () => ({}),
    errors: () => ({}),
  }
);

const emit = defineEmits(['update:modelValue']);

// modelValue is separated by \n - multiple descriptions
const descriptions = computed({
  get: (): string[] => {
    if (!props.modelValue) {
      return [''];
    }
    const lines = props.modelValue.split('\n');
    return lines.length === 0 ? [''] : lines;
  },
  set: (newList: string[]) => {
    // Join the array back into a single string, preserving empty lines
    const newValue = newList.join('\n');
    emit('update:modelValue', newValue);
  },
});
const maxDescriptions = computed(() => props.options?.max_lines);
const canAddDescription = computed(() => {
  return !maxDescriptions.value || descriptions.value.length < maxDescriptions.value;
});

/**
 * Updates a specific description in the list based on its index.
 */
const updateDescription = (index: number, newValue: string) => {
  const updatedList = [...descriptions.value];
  updatedList[index] = newValue;
  descriptions.value = updatedList;
};

const addDescription = () => {
  if (!canAddDescription.value) return;
  descriptions.value = [...descriptions.value, ''];
};
</script>

<style scoped>
:deep(.form-label) {
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  margin-bottom: 6px;
  color: #344054;
}

.float-right {
  float: right;
}
</style>
